import { Injectable, inject } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { auditTime, combineLatest, filter, map, switchMap, take, tap } from "rxjs";
import { CARD_SIZE_TO_SHORT } from "../../enums/table/card-size.enum";
import { TableModeEnum } from "../../enums/table/table-mode-enum";
import { TableActions } from "./table-actions";
import { TableSelector } from "./table-selectors";

// Mirrors the view (list/card) + card size to the URL as compact query params so
// navigating away and back restores the same view. Mirror of TableFilterEffects.
//   tv = 'card' (omitted when list — the default)
//   cs = s | m | f
// Uses merge + replaceUrl so toggling never adds a history entry (Back skips past
// view toggles). The params are deliberately distinct from the slide-in params
// (si/siid/sitab/ss/tl), so closing a slide-in (merge-reset of those) never disturbs them.
@Injectable()
export class TableViewEffects {
    actions$ = inject(Actions);
    store = inject(Store);
    router = inject(Router);
    activatedRoute = inject(ActivatedRoute);

    syncViewParams$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(TableActions.setTableMode, TableActions.setCardSize),
                auditTime(0),
                // Never steer the router while a navigation is in flight. navigate([]) targets the
                // CURRENT url - the page being left - and a navigation issued mid-flight supersedes
                // the one in progress: the user clicked /B/dashboard and lands back on /A/products,
                // with the store already switched to B. setTableMode arrives asynchronously (the user
                // settings load), so it can land exactly there. Same guard as TableFilterEffects.
                filter(() => !this.router.getCurrentNavigation()),
                switchMap(action =>
                    combineLatest([
                        this.store.select(TableSelector.tableMode(action.tableId)).pipe(take(1)),
                        this.store.select(TableSelector.cardSize(action.tableId)).pipe(take(1)),
                    ]).pipe(
                        map(([mode, size]) => ({
                            tv: mode === TableModeEnum.Card ? 'card' : null,
                            cs: size ? CARD_SIZE_TO_SHORT[size] : null,
                        }))
                    )
                ),
                tap(({ tv, cs }) => {
                    this.router.navigate([], {
                        relativeTo: this.activatedRoute,
                        queryParams: { tv, cs },
                        queryParamsHandling: 'merge',
                        replaceUrl: true,
                    });
                })
            ),
        { dispatch: false }
    );
}
